import { useState } from "react";
import ToastContainer from 'react-bootstrap/ToastContainer';
import Toast from 'react-bootstrap/Toast';
import CloseButton from 'react-bootstrap/CloseButton';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import React from "react";
import { generateTileSVG } from '../utils/tileGenerator';
import { COLORS } from "../utils/colors";
import useImage from '../utils/useImage';

interface VictoryModalProps {
  onVictoryClose: ()=>void;
  showModal: boolean;
  elapsedTime: string;
  gameNumber: number;
  streak: number;
  maxStreak: number;
  wins: number;
  hintsUsed: number;
  userId?: string;
  gameTiles: string[];
  mode: string;
  onSwitchMode: () => void;
  isSpecialPuzzle?: boolean;
  showTimePercentile?: boolean;
  timePercentile?: number | null;
}

// Component to render a single tile (handles both webp and SVG)
function VictoryTile({ tile, index }: { tile: string; index: number }) {
  const isWebp = tile.endsWith('.webp');
  const { image } = useImage(isWebp ? tile : '');
  
  if (isWebp) {
    return image ? (
      <img
        src={image}
        alt="tile"
        width={75}
        height={75}
        style={{ background: 'white' }}
      />
    ) : null;
  }
  
  return (
    <img
      src={`data:image/svg+xml,${encodeURIComponent(generateTileSVG(tile, 75))}`}
      alt="tile"
      width={75}
      height={75}
      style={{ background: 'white' }}
    />
  );
}

function VictoryModal(props: VictoryModalProps) {
  const [showToast, setShowToast] = useState(false);

  const bulbs = 
    props.hintsUsed === 3 ? '💡💡💡' :
    props.hintsUsed === 2 ? '💡💡' :
    props.hintsUsed === 1 ? '💡' : ' ';
  
  const showPercentile =
    props.showTimePercentile === true &&
    Number.isFinite(props.timePercentile);

  const copyResults = () => {
    navigator.clipboard.writeText(
      `Puzzli # ${props.gameNumber}\n` +
      `${props.mode === 'casual' ? '🟦' : '⬛'} ${props.elapsedTime}${bulbs}`
    );
    setShowToast(true);
  }

  return (
    <>
      <Modal
        show={props.showModal}
        className='victory-modal'
        size='sm'
        animation={true}
      >
        <div className="close-button-container">
          <CloseButton onClick={() => props.onVictoryClose()} />
        </div>
        <Modal.Body style={{marginTop: 5}}>
          <div style={{fontSize: 48, fontWeight: 'bold'}}> Congrats! </div>
          <div style={{fontSize: 32}}> Puzzli # {props.gameNumber} </div>
          <div style={{fontSize: 16}}> {props.mode === 'casual' ? 'Casual' : 'Classic'} Mode </div>
          {props.isSpecialPuzzle && (
            <div style={{fontSize: 14, fontStyle: 'italic', marginTop: 5}}> Thanks for playing! </div>
          )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 75px)',
            gridTemplateRows: 'repeat(3, 75px)',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 32, 
            marginTop: 10,
            marginBottom: 10
          }}>
            {props.gameTiles.map((tile, i) => (
              <VictoryTile key={i} tile={tile} index={i} />
            ))}
          </div>
          {props.hintsUsed > 0 && (
            <div style={{ fontSize: 16, marginTop: 5 }}>
              {bulbs} {props.hintsUsed} {props.hintsUsed === 1 ? "Hint Used" : "Hints Used"}
            </div>
          )}
          <div className="victory-hero-cluster">
            <div className="victory-time">{props.elapsedTime}</div>
            {showPercentile && (
              <div className="victory-percentile">
                Faster than <strong>{props.timePercentile}%</strong> of players today
              </div>
            )}
          </div>
          <div className="victory-lifetime">
            <span>Wins {props.wins}</span>
            <span>Streak {props.streak}</span>
            <span>Best streak {props.maxStreak}</span>
          </div>
        </Modal.Body>
        <Modal.Footer style={{textAlign: 'center', justifyContent: 'center'}}>
          <Button
            onClick={props.onSwitchMode}
            style={{ margin: 10, width: 125, backgroundColor: props.mode === "classic" ? COLORS.BLUE : COLORS.BLACK, borderColor: props.mode === "classic" ? COLORS.BLUE : COLORS.BLACK }}>
            {props.mode === "classic" ? (
		<>
			Play<br />Casual
		</>
	) : (
		<>
			Play<br />Classic
		</>
	)}
          </Button>
          <Button 
            className="game-button"
            onClick={() => copyResults()} 
            style={{margin: 10, width: 125}}>
            Share<br />Results
          </Button>
        </Modal.Footer>
        <ToastContainer position="middle-center">
          <Toast show={showToast} onClose={() => setShowToast(false)} delay={2000} autohide>
            <Toast.Body>Results copied to clipboard</Toast.Body>
          </Toast>
        </ToastContainer>
      </Modal>
    </>
  );
}

export default VictoryModal;
